import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "~/contexts/analytics-context";
import { copyToClipboard } from "~/utils/helpers";
import { toast } from "~/utils/toast";
import { useProduct } from "~/contexts/product-context";
import { useErrorToast } from "~/hooks/use-error-toast";
import { BaseItem } from "~/components/sortable-list";
import {
  createListItems,
  createManualSubject,
} from "~/pages/product-details/tabs/thema-tab/utils";
import { googleAnalyticsKeywordSelectionReport } from "~/shared/services/keyword-service";
import {
  countKeywordCharacters,
  getKeywordsByType,
} from "~/integrations/firebase/collections";

import { useSubjects } from "~/hooks/use-subjects";
import { phraseToWords } from "~/components/keywords-tab/utils/keyword-utils";
import { useKeywordGeneration } from "~/components/keywords-tab/hooks/use-keyword-generation";
import { useKeywordLimitRecommendationToast } from "~/components/keywords-tab/hooks/use-keyword-limit-recommendation-toast";
import { FinalKeywordsView } from "./final-keywords-view";

interface FinalKeywordsProps {
  showReasoning?: boolean;
}

const countListItemCharacters = (items: BaseItem[]) =>
  items.reduce((totalChars, item) => {
    const keyword = item.value.trim();

    return keyword ? totalChars + keyword.length : totalChars;
  }, 0);

export const FinalKeywords = ({
  showReasoning = false,
}: FinalKeywordsProps) => {
  const { t } = useTranslation(["components"]);
  const { product } = useProduct();
  const { gaEvent } = useAnalytics();
  const { final: finalKeywords, finalWords } = getKeywordsByType(product);
  const { generateKeywords, isGenerating, errorUpdate } =
    useKeywordGeneration();
  const {
    handleToggleSubject,
    handleSaveFinalKeywordItems,
    handleChangeManualSubject,
    handleChangeWordOrder,
  } = useSubjects();

  const errorMessage = useMemo(() => {
    if (errorUpdate) {
      return t("components:keywordsTab.finalKeywords.toasts.errorGenerate");
    }
    return null;
  }, [errorUpdate, t]);

  useErrorToast(errorMessage);

  const finalKeywordsListItems = useMemo(() => {
    const items = createListItems(finalKeywords);
    return items.map((item, index) => {
      const reasoning = finalKeywords[index]?.rankingReasoning;
      const subtitle = showReasoning
        ? (reasoning ??
          t("components:keywordsTab.finalKeywords.phraseReasoning.noMatch"))
        : undefined;
      return {
        ...item,
        infoText: undefined,
        subtitle,
      };
    });
  }, [finalKeywords, showReasoning, t]);

  const [pendingRemovedFinalKeywordIds, setPendingRemovedFinalKeywordIds] =
    useState<Set<string>>(() => new Set());

  const finalWordsListItems = useMemo(() => {
    const items = createListItems(finalWords);
    return items.map((item, index) => {
      const wordSubject = finalWords[index];
      const wordLower = wordSubject?.subjectHeadingText?.trim().toLowerCase();
      const phrases = wordLower
        ? finalKeywords
            .map((phrase) => phrase.subjectHeadingText?.trim().toLowerCase())
            .filter(
              (phrase) => phrase && phraseToWords(phrase).includes(wordLower),
            )
        : [];
      const hotnessScore = wordSubject?.hotnessScore;
      const displayScore =
        hotnessScore == null ? "-" : Math.round(hotnessScore);
      const subtitle =
        phrases.length === 0
          ? undefined
          : t("components:keywordsTab.finalKeywords.wordReasoning.comesFrom", {
              score: displayScore,
              phrases: phrases.join(", "),
            });
      return {
        ...item,
        infoText: undefined,
        subtitle: showReasoning ? subtitle : undefined,
      };
    });
  }, [finalWords, finalKeywords, showReasoning, t]);

  const displayedFinalKeywordsListItems = useMemo(
    () =>
      finalKeywordsListItems.filter(
        (item) => !pendingRemovedFinalKeywordIds.has(item.id),
      ),
    [finalKeywordsListItems, pendingRemovedFinalKeywordIds],
  );

  useEffect(() => {
    const liveFinalKeywordIds = new Set(
      finalKeywordsListItems.map((item) => item.id),
    );

    // reconciles optimistic-removal ids with server-backed keywords; preserves the reference for no-op reconciliation and updates only when stale ids must be pruned
    setPendingRemovedFinalKeywordIds((current) => {
      const next = new Set(
        Array.from(current).filter((id) => liveFinalKeywordIds.has(id)),
      );

      return next.size === current.size ? current : next;
    });
  }, [finalKeywordsListItems]);

  const finalKeywordsCharacters = countListItemCharacters(
    displayedFinalKeywordsListItems,
  );
  const finalWordsCharacters = countKeywordCharacters(finalWords);

  useKeywordLimitRecommendationToast({
    finalKeywordsCharacters,
    finalWordsCharacters,
  });

  const onCopyPhrases = useCallback(async () => {
    gaEvent({
      type: "copy_keywords_from_final_selection",
      payload: googleAnalyticsKeywordSelectionReport(product),
    });

    await copyToClipboard({
      arrayToCopy: displayedFinalKeywordsListItems.map((item) => item.value),
    });

    toast.success(t("components:keywordsTab.finalKeywords.toasts.copy.header"));
  }, [displayedFinalKeywordsListItems, t, gaEvent, product]);

  const onCopyWords = useCallback(async () => {
    gaEvent({
      type: "copy_keywords_from_final_selection",
      payload: googleAnalyticsKeywordSelectionReport(product),
    });

    await copyToClipboard({
      arrayToCopy: finalWords.map((word) => word.subjectHeadingText ?? ""),
    });

    toast.success(t("components:keywordsTab.finalKeywords.toasts.copy.header"));
  }, [finalWords, t, gaEvent, product]);

  const handleChangeWordOrderWrapper = useCallback(
    (items: BaseItem[]) => {
      handleChangeWordOrder(items);
    },
    [handleChangeWordOrder],
  );

  const onSaveKeyword = useCallback(
    async (keyword: string) => {
      if (!product) return;

      const exists = getKeywordsByType(product).final.some(
        (k) => k.subjectHeadingText === keyword,
      );

      if (exists) {
        toast.error(
          t(
            "components:keywordsTab.finalKeywords.toasts.info.keywordAlreadyExists",
          ),
        );
        gaEvent({
          type: "add_custom_keyword_to_final_selection",
          payload: {
            type: "exists_already",
            keyword,
            book: product.id,
          },
        });
        return;
      }

      try {
        const manualSubject = createManualSubject(keyword);
        await handleToggleSubject(manualSubject);
        gaEvent({
          type: "add_custom_keyword_to_final_selection",
          payload: {
            type: "confirm",
            book: product.id,
            keyword,
          },
        });
      } catch {
        toast.error(
          t("components:keywordsTab.finalKeywords.toasts.errorUpdate"),
        );
      }
    },
    [handleToggleSubject, product, gaEvent, t],
  );

  const onCancelKeyword = useCallback(
    (keyword: string) => {
      if (!product) return;
      gaEvent({
        type: "add_custom_keyword_to_final_selection",
        payload: { type: "cancel", book: product.id, keyword },
      });
    },
    [product, gaEvent],
  );

  const onRemove = useCallback(
    (item: BaseItem | undefined) => {
      if (!item || !product) return;

      const subjectItem = finalKeywords.find((s) => s.id === item.id);
      if (subjectItem) {
        setPendingRemovedFinalKeywordIds((current) =>
          new Set(current).add(item.id),
        );
        const itemsAfterRemove = displayedFinalKeywordsListItems.filter(
          (currentItem) => currentItem.id !== item.id,
        );
        const updatePromise = handleSaveFinalKeywordItems(
          itemsAfterRemove,
        ).catch((error) => {
          setPendingRemovedFinalKeywordIds((current) => {
            const next = new Set(current);
            next.delete(item.id);
            return next;
          });
          throw error;
        });
        gaEvent({
          type: "remove_keyword_from_final_selection",
          payload: {
            book: product.id,
            keyword: subjectItem.subjectHeadingText ?? "",
          },
        });
        return updatePromise;
      }

      return Promise.resolve();
    },
    [
      displayedFinalKeywordsListItems,
      finalKeywords,
      handleSaveFinalKeywordItems,
      gaEvent,
      product,
    ],
  );

  const onGenerate = useCallback(async () => {
    if (!product) return;
    await generateKeywords();
  }, [product, generateKeywords]);

  return (
    <FinalKeywordsView
      data={{
        phrases: displayedFinalKeywordsListItems,
        words: finalWordsListItems,
        phrasesCharacters: finalKeywordsCharacters,
        wordsCharacters: finalWordsCharacters,
      }}
      status={{ isGenerating }}
      onGenerate={onGenerate}
      onChangeOrder={handleSaveFinalKeywordItems}
      onChangeWordOrder={handleChangeWordOrderWrapper}
      onRemove={onRemove}
      onEdit={handleChangeManualSubject}
      onSaveKeyword={onSaveKeyword}
      onCancelKeyword={onCancelKeyword}
      onCopyPhrases={onCopyPhrases}
      onCopyWords={onCopyWords}
    />
  );
};
